import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Upload, RotateCcw, Globe, ZoomIn, ZoomOut, CheckCircle2, Circle, Lightbulb } from 'lucide-react';
import { POINT_KEYS, TOTAL_POINTS, computeResults } from '../lib/cephalometry';
import PredictionModal from '../components/cephalometry/PredictionModal';
import ResultsPanel from '../components/cephalometry/ResultsPanel';

const POINT_COLORS = [
  'rgba(255, 68, 68, 0.6)',   // S
  'rgba(68, 255, 68, 0.6)',   // N
  'rgba(68, 136, 255, 0.6)',  // A
  'rgba(255, 221, 68, 0.6)',  // B
  'rgba(255, 159, 67, 0.6)',  // SpNa
  'rgba(255, 159, 67, 0.6)',  // SpNз
  'rgba(162, 155, 254, 0.6)', // Me
  'rgba(162, 155, 254, 0.6)'  // Go
];
const POINT_RADIUS = 10;
const MAX_DIM = 900;
const MAX_FILE_MB = 25;
const LANGS = ['ru', 'en', 'pt'];
const LANG_PATH = { ru: '/metrica', en: '/en/metrica', pt: '/pt/metrica' };

// Положение подсказки на обучающем снимке, % от размера изображения
const TUTORIAL_POINTS = [
    { key: 'S', x: 28.5, y: 29 },
    { key: 'N', x: 72, y: 24 },
    { key: 'A', x: 73, y: 57 },
    { key: 'B', x: 70.5, y: 79 },
    { key: 'SpNa', x: 77, y: 54 },
    { key: 'SpNз', x: 42, y: 55 },
    { key: 'Me', x: 66, y: 90.5 },
    { key: 'Go', x: 26, y: 74 }
];

const CephalometricPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const imgRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const [points, setPoints] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [dragging, setDragging] = useState(-1);
    const [canvasSize, setCanvasSize] = useState({ width: 900, height: 600 });
    const [hasImage, setHasImage] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedPrediction, setSelectedPrediction] = useState(null);
    const [patientName, setPatientName] = useState(''); // ФИО пациента (нужно для работы и попадает в .docx)

    // Подсказка видна, пока на загруженном снимке расставлены не все точки
    const showTutorial = hasImage && currentStep < TOTAL_POINTS;

    // Язык берём из адреса: /metrica, /en/metrica, /pt/metrica
    useEffect(() => {
        const first = location.pathname.split('/').filter(Boolean)[0];
        const lang = LANGS.includes(first) ? first : 'ru';
        if ((i18n.language || '').slice(0, 2) !== lang) i18n.changeLanguage(lang);
    }, [location.pathname, i18n]);

    // Переключение языка по кругу: ru → en → pt → ru
    const nextLang = LANGS[(LANGS.indexOf((i18n.language || 'ru').slice(0, 2)) + 1) % LANGS.length];
    const handleLanguageSwitch = () => {
        i18n.changeLanguage(nextLang);
        navigate(LANG_PATH[nextLang]);
    };

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = imgRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!img) {
            ctx.fillStyle = '#666';
            ctx.font = '20px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(t('metrica:canvas.placeholder'), canvas.width / 2, canvas.height / 2);
            return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        if (points.length >= 2) {
            ctx.strokeStyle = 'rgba(0,0,0,0.3)';
            ctx.lineWidth = 3;
            for (let i = 0; i < points.length - 1; i++) {
                if (i !== 5 && i !== 3) { // отрезки 4→5 (B–SpNa) и 6→7 (SpNz–Me) не рисуем
                    ctx.beginPath();
                    ctx.moveTo(points[i].x, points[i].y);
                    ctx.lineTo(points[i + 1].x, points[i + 1].y);
                    ctx.stroke();
                }
            }
        }
       
        if (points.length > 3) {
            const pointN = points[1]; // Индекс 1 соответствует точке N
            const pointB = points[3]; // Индекс 3 соответствует точке B
            
            if (pointN && pointB) {
                ctx.beginPath();
                ctx.strokeStyle = 'rgba(36, 36, 36, 0.5)'; // линия N–B
                ctx.lineWidth = 3;
                ctx.moveTo(pointN.x, pointN.y);
                ctx.lineTo(pointB.x, pointB.y);
                ctx.stroke();
            }
        }

        const markerRadius = 10;
        points.forEach((pt, i) => {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1.5;
            const crossLen = markerRadius * 0.75;
            ctx.beginPath();
            ctx.moveTo(pt.x - crossLen, pt.y); ctx.lineTo(pt.x + crossLen, pt.y);
            ctx.moveTo(pt.x, pt.y - crossLen); ctx.lineTo(pt.x, pt.y + crossLen);
            ctx.stroke();

            if (i === dragging) {
                ctx.beginPath();
                ctx.arc(pt.x, pt.y, 22, 0, Math.PI * 2);
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            ctx.fillStyle = '#fff';
            ctx.font = 'bold 16px sans-serif';
            ctx.textAlign = 'left';
            ctx.fillText(POINT_KEYS[i], pt.x + 12, pt.y + 4);
        });
    }, [points, dragging, t]);

    useEffect(() => { draw(); }, [draw]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        e.target.value = ''; // чтобы тот же файл можно было выбрать повторно
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error(t('metrica:errors.notImage', 'Выберите файл изображения')); return; }
        if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(t('metrica:errors.tooLarge', { mb: MAX_FILE_MB, defaultValue: 'Файл слишком большой (максимум {{mb}} МБ)' })); return; }
        const reader = new FileReader();
        reader.onerror = () => toast.error(t('metrica:errors.readFailed', 'Не удалось прочитать файл'));
        reader.onload = (ev) => {
            const image = new Image();
            image.onerror = () => toast.error(t('metrica:errors.badImage', 'Не удалось открыть изображение'));
            image.onload = () => {
                imgRef.current = image;
                let w = image.width*1.2, h = image.height*1.2;
                if (Math.max(w, h) > MAX_DIM) {
                    const scale = MAX_DIM / Math.max(w, h);
                    w = Math.round(w * scale); h = Math.round(h * scale);
                }
                setCanvasSize({ width: w, height: h });
                setPoints([]); setCurrentStep(0); setHasImage(true); setZoom(1);
            };
            image.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleReset = () => { setPoints([]); setCurrentStep(0); setDragging(-1); setZoom(1); };
    
    const handleDeletePoint = (idx) => {
        if (idx < points.length) {
            const newPoints = [...points]; newPoints.splice(idx, 1);
            setPoints(newPoints); setCurrentStep(newPoints.length);
        }
    };
    
    const getCanvasCoords = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };
    };
    
    const handleCanvasMouseDown = (e) => {
        // Блокируем взаимодействие, если не введено ФИО
        if (!imgRef.current || !patientName.trim()) return;
        const { x: mx, y: my } = getCanvasCoords(e);
        for (let i = points.length - 1; i >= 0; i--) {
            const dx = points[i].x - mx, dy = points[i].y - my;
            if (dx * dx + dy * dy <= POINT_RADIUS * POINT_RADIUS) { setDragging(i); return; }
        }
        if (currentStep < TOTAL_POINTS) {
            setPoints([...points, { x: mx, y: my }]); setCurrentStep(currentStep + 1);
        }
    };
    
    const handleCanvasMouseMove = (e) => {
        if (dragging < 0) return;
        const { x, y } = getCanvasCoords(e);
        const newPoints = [...points]; newPoints[dragging] = { x, y }; setPoints(newPoints);
    };
    
    const handleCanvasMouseUp = () => setDragging(-1);
    
    const handleTouchStart = (e) => {
        // Блокируем взаимодействие, если не введено ФИО
        if (!imgRef.current || !patientName.trim()) return;
        e.preventDefault();
        const touch = e.touches[0], rect = canvasRef.current.getBoundingClientRect();
        const mx = (touch.clientX - rect.left) / zoom, my = (touch.clientY - rect.top) / zoom;
        for (let i = points.length - 1; i >= 0; i--) {
            const dx = points[i].x - mx, dy = points[i].y - my;
            if (dx * dx + dy * dy <= (POINT_RADIUS + 5) ** 2) { setDragging(i); return; }
        }
        if (currentStep < TOTAL_POINTS) {
            setPoints([...points, { x: mx, y: my }]); setCurrentStep(currentStep + 1);
        }
    };
    
    const handleTouchMove = (e) => {
        if (dragging < 0) return; e.preventDefault();
        const touch = e.touches[0], rect = canvasRef.current.getBoundingClientRect();
        const x = (touch.clientX - rect.left) / zoom, y = (touch.clientY - rect.top) / zoom;
        const newPoints = [...points]; newPoints[dragging] = { x, y }; setPoints(newPoints);
    };
    
    const handleTouchEnd = () => setDragging(-1);
    const handleZoomIn = () => setZoom(p => Math.min(p + 0.25, 3));
    const handleZoomOut = () => setZoom(p => Math.max(p - 0.25, 0.5));
    const handleShowDetails = (code) => { setSelectedPrediction(code); setShowModal(true); };
    const closeModal = () => { setShowModal(false); setSelectedPrediction(null); };

    const results = computeResults(points);

    const pointDescriptions = POINT_KEYS.map(key => ({
        key,
        name: t(`metrica:points.${key}.name`),
        hint: t(`metrica:points.${key}.hint`)
    }));
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef] text-[#2c3e50]">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#dee2e6] shadow-md">
                <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <button onClick={() => navigate(-1)} className="flex items-center gap-3 text-[#e74c3c] hover:text-[#c0392b] transition-colors px-4 py-3 rounded-xl hover:bg-[#f8f9fa] shadow-sm">
                            <ArrowLeft size={24} /><span className="hidden sm:inline text-lg font-semibold">{t('metrica:header.back')}</span>
                        </button>
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2c3e50] text-center">{t('metrica:header.title')}</h1>
                        {/* Обновлённая кнопка смены языка с динамическим кодом */}
                        <button onClick={handleLanguageSwitch} className="flex items-center gap-3 text-xl hover:text-[#3498db] transition-colors px-4 py-3 rounded-xl hover:bg-[#f8f9fa] shadow-sm">
                            <Globe size={24} /><span className="hidden sm:inline text-lg font-semibold">{nextLang.toUpperCase()}</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Controls */}
            <div className="bg-white/80 border-b border-[#dee2e6] px-4 py-4 shadow-sm">
                <div className="max-w-[1600px] mx-auto flex gap-4 justify-center flex-wrap">
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                    <button onClick={() => fileInputRef.current?.click()} className="bg-gradient-to-r from-[#3498db] to-[#2980b9] text-white px-6 py-3.5 rounded-xl hover:from-[#2980b9] hover:to-[#2472a4] transition-all hover:shadow-xl hover:shadow-[#3498db]/30 flex items-center gap-3 font-bold text-lg"><Upload size={22} />{t('metrica:controls.loadImage')}</button>
                    <button onClick={handleReset} disabled={!hasImage} className="bg-gradient-to-r from-[#95a5a6] to-[#7f8c8d] text-white px-6 py-3.5 rounded-xl hover:from-[#7f8c8d] hover:to-[#6c7a7d] transition-all disabled:bg-[#ecf0f1] disabled:cursor-not-allowed disabled:text-[#95a5a6] flex items-center gap-3 font-bold text-lg shadow-md"><RotateCcw size={22} />{t('metrica:controls.reset')}</button>
                </div>
            </div>

            {/* Поле ввода ФИО пациента */}
            {hasImage && (
                <div className="bg-white/80 border-b border-[#dee2e6] px-4 py-4 shadow-sm">
                    <div className="max-w-[1600px] mx-auto flex gap-4 justify-center items-center flex-wrap">
                        <label htmlFor="patientName" className="text-lg font-bold text-[#2c3e50]">
                            {t('metrica:controls.patientName', 'ФИО пациента')}:
                        </label>
                        <input
                            id="patientName"
                            type="text"
                            value={patientName}
                            onChange={(e) => setPatientName(e.target.value)}
                            className="px-4 py-2 border-2 border-[#ced4da] rounded-xl text-lg focus:outline-none focus:border-[#3498db] w-full max-w-md"
                            placeholder={t('metrica:controls.patientNamePlaceholder', 'Введите ФИО')}
                        />
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="flex flex-col xl:flex-row gap-5 p-5 max-w-[2000px] mx-auto">
                {/* Canvas + Tutorial Section */}
                    <div className="flex flex-col xl:flex-row gap-5">
                    {/* Canvas */}
                    <div className="flex-[1] min-w-0">
                        <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-[#dee2e6]">
                            {hasImage && (
                                <div className="flex justify-between items-center px-5 py-3 bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] border-b border-[#dee2e6]">
                                    <span className="text-lg text-[#495057] font-medium">{t('metrica:canvas.instruction')}</span>
                                    <div className="flex gap-3">
                                        <button onClick={handleZoomOut} className="p-3 rounded-xl bg-white hover:bg-[#e9ecef] transition-colors shadow-sm border border-[#ced4da]"><ZoomOut size={22} className="text-[#495057]" /></button>
                                        <span className="px-4 py-3 text-lg font-bold font-mono bg-white rounded-xl shadow-sm border border-[#ced4da] text-[#2c3e50]">{Math.round(zoom * 100)}%</span>
                                        <button onClick={handleZoomIn} className="p-3 rounded-xl bg-white hover:bg-[#e9ecef] transition-colors shadow-sm border border-[#ced4da]"><ZoomIn size={22} className="text-[#495057]" /></button>
                                    </div>
                                </div>
                            )}
                            <div ref={canvasContainerRef} className="relative overflow-auto bg-[#fafafa]" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                                {/* Оверлей с просьбой ввести ФИО */}
                                {hasImage && !patientName.trim() && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 pointer-events-none">
                                        <p className="text-white text-xl font-bold bg-black/70 px-6 py-3 rounded-xl text-center">
                                            {t('metrica:canvas.enterName', 'Пожалуйста, введите ФИО пациента для начала работы')}
                                        </p>
                                    </div>
                                )}
                                <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} className="block" style={{ cursor: dragging !== -1 ? 'grabbing' : 'pointer', width: `${canvasSize.width * zoom}px`, height: `${canvasSize.height * zoom}px`, transition: dragging >= 0 ? 'none' : 'width 0.2s, height 0.2s' }} 
                                    onMouseDown={handleCanvasMouseDown} onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp} onMouseLeave={handleCanvasMouseUp} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
                            </div>
                        </div>
                    </div>

                    {/* Tutorial Panel */}
                    {showTutorial && hasImage && currentStep < TOTAL_POINTS && (
                        <div className="xl:w-[520px] shrink-0 bg-white rounded-2xl p-6 shadow-xl border-2 border-[#3498db] animate-fadeIn">
                            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#dee2e6]">
                                <Lightbulb className="text-[#f39c12] flex-shrink-0" size={28} />
                                <h3 className="text-2xl font-bold text-[#2c3e50]">{t('metrica:tutorial.title')}</h3>
                            </div>
                            <div className="relative bg-[#f8f9fa] rounded-xl overflow-hidden border border-[#dee2e6] mb-5">
                                <img src="/tutor.jpg" alt="Cephalometric tutorial" className="w-full h-auto object-contain" />
                                {currentStep < TOTAL_POINTS && (
                                    <div className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{ left: `${TUTORIAL_POINTS[currentStep].x}%`, top: `${TUTORIAL_POINTS[currentStep].y}%` }}>
                                        <div className="relative w-7 h-7">
                                            <div className="absolute w-7 h-7 inset-0 rounded-full animate-ping opacity-120" style={{ backgroundColor: POINT_COLORS[currentStep], animationDuration: '1.5s' }} />
                                            <div className="absolute w-3 h-3 left-2 top-2 inset-0 rounded-full border-2 border-black shadow-lg flex items-center justify-center">
                                            <div className="relative w-7 h-7">
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div key={currentStep} className="flex items-center gap-5 py-3 px-5 rounded-xl transition-all border-2 bg-gradient-to-r from-[#f8d7da] to-[#f5c6cb] border-[#e74c3c] animate-pulse shadow-md">
                                
                                <div className="flex items-center gap-5 mb-2">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md" style={{ backgroundColor: POINT_COLORS[currentStep] }}>
                                        {TUTORIAL_POINTS[currentStep].key}
                                    </div>
                                    <span className="font-bold flex text-[#2c3e50] text-xl">{pointDescriptions[currentStep]?.name}</span>
                                    <p className="text-[#000] flex text-lg leading-relaxed">{pointDescriptions[currentStep]?.hint}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Results Block (Horizontal) */}
                {!showTutorial && hasImage && results &&(
                <ResultsPanel results={results} handleShowDetails={handleShowDetails}/>
                )}

                {/* Side Panel - Results */}
                    <div className="xl:w-[400px] space-y-5">
                    {/* Points Block */}
                    <div className="bg-white rounded-2xl p-6 shadow-xl border border-[#dee2e6]">
                        <h2 className="text-[#2c3e50] font-bold mb-5 text-2xl flex items-center gap-3"><Circle size={22} className="text-[#3498db]" />{t('metrica:placement.title')}</h2>
                        <div className="space-y-3 max-h-[660px] overflow-y-auto pr-3">
                            {pointDescriptions.map((desc, i) => (
                                <div key={i} className={`flex items-center gap-5 py-4 px-5 rounded-xl transition-all border-2 ${i < currentStep ? 'bg-gradient-to-r from-[#d4edda] to-[#c3e6cb] border-[#28a745] shadow-sm' : i === currentStep ? 'bg-gradient-to-r from-[#f8d7da] to-[#f5c6cb] border-[#e74c3c] animate-pulse shadow-md' : 'bg-[#f8f9fa] border-[#e9ecef] hover:border-[#ced4da]'}`}>
                                    <div className="flex-shrink-0">{i < currentStep ? <CheckCircle2 size={24} className="text-[#28a745]" /> : <Circle size={24} className={i === currentStep ? 'text-[#e74c3c]' : 'text-[#adb5bd]'} />}</div>
                                    <div className="w-7 h-7 rounded-full flex-shrink-0 relative shadow-md" style={{ backgroundColor: POINT_COLORS[i] }}><div className="absolute inset-0 flex items-center justify-center"><div className="w-3 h-3 rounded-full bg-white border-2 border-[#333]" /></div></div>
                                    <div className="flex-1 min-w-0"><div className="text-xl font-bold truncate text-[#2c3e50]">{desc.key}: {desc.name}</div>{i === currentStep && <div className="text-base text-[#e74c3c] mt-1 font-medium">{desc.hint}</div>}</div>
                                    {i < points.length && <button onClick={() => handleDeletePoint(i)} className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-xl text-[#6c757d] hover:text-[#e74c3c] hover:bg-[#f8d7da] transition-all text-xl font-bold shadow-sm" title={t('metrica:placement.delete')}>✕</button>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Block (Horizontal) */}
            {(showTutorial || !hasImage) && (
            <ResultsPanel results={results} handleShowDetails={handleShowDetails}/>
            )}
            {showModal && selectedPrediction && (
                <PredictionModal 
                    code={selectedPrediction} 
                    onClose={closeModal} 
                    patientName={patientName}
                    results={results}
                />
            )}
        </div>
    );
};

export default CephalometricPage;